import React, { useEffect, useRef } from "react";
import { FaArrowRight, FaPlay } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";

const Hero = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const buttonsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add visible class to trigger animations
            entry.target.classList.add("visible");
            
            // Stagger button animations
            buttonsRef.current.forEach((button, index) => {
              setTimeout(() => {
                if (button) button.classList.add("visible");
              }, index * 100 + 800);
            });
            
            // Add floating animation after entrance
            setTimeout(() => {
              entry.target.classList.add("animated");
            }, 1000);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  const setButtonRef = (el, index) => {
    buttonsRef.current[index] = el;
  };

  return (
    <section id="home" className="hero" ref={heroRef}>
      {/* Background Elements */}
      <div className="hero-background">
        <div className="grid-overlay"></div>
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
      </div>

      <div className="container">
        <div className="hero-content">
          {/* Badge */}
        
          {/* Main Title */}
          <div className="title-wrapper">
            <h1 className="hero-title">
              <span className="title-line title-line-1">
                Building Smart Digital
              </span>
              <span className="title-line title-line-2">
                Solutions{' '}
                <span className="highlight-text"> {' '}For Modern</span>
              </span>
              <span className="title-line title-line-3">
                Businesses
              </span>
            </h1>
          </div>

          {/* Tagline */}
          <p className="tagline">
            <span className="company-name">Codriva Tech</span>
            – A Technology-Driven IT Company Delivering
            Scalable Web, Mobile & Software Solutions
          </p>

          {/* Description */}
          <p className="description">
            We help startups, enterprises, and growing businesses transform
            ideas into powerful digital products. Our expertise includes
            custom software development, web & mobile applications,
            cloud solutions, UI/UX design, and long-term technical support.
          </p>

      

          {/* Buttons */}
          <div className="hero-actions">
            <a 
              href="#services" 
              className="btn-primary"
              ref={(el) => setButtonRef(el, 0)}
            >
              <span className="btn-text">Explore Services</span>
              <span className="btn-icon">
                <FiChevronRight />
              </span>
              <span className="btn-hover-effect"></span>
            </a>
            
            <a 
              href="#contact" 
              className="btn-secondary"
              ref={(el) => setButtonRef(el, 1)}
            >
              <span className="btn-text">Get Free Consultation</span>
              <span className="btn-icon">
                <FaArrowRight />
              </span>
            </a>

          
          </div>

        </div>
      </div>

      <style jsx>{`
        .hero {
          min-height: 100vh;
          padding: 160px 0 100px;
          background: linear-gradient(135deg, 
            #020617 0%,
            #0f172a 30%,
            #1e293b 70%,
            #0f172a 100%);
          color: #f8fafc;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
        }

        /* Background Elements */
        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 1;
        }

        .grid-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            linear-gradient(rgba(34, 211, 238, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34, 211, 238, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          mask-image: radial-gradient(circle at center, black 30%, transparent 70%);
        }

        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0;
          transition: opacity 1.5s ease;
        }

        .hero.visible .gradient-orb {
          opacity: 0.4;
        }

        .orb-1 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, #22d3ee, transparent 70%);
          top: -200px;
          right: -100px;
        }

        .orb-2 {
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, #3b82f6, transparent 70%);
          bottom: -150px;
          left: -100px;
        }

        .orb-3 {
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, #8b5cf6, transparent 70%);
          top: 50%;
          left: 10%;
        }

        .floating-shape {
          position: absolute;
          background: rgba(34, 211, 238, 0.1);
          border-radius: 10px;
          animation: float 15s ease-in-out infinite;
        }

        .shape-1 {
          width: 40px;
          height: 40px;
          top: 20%;
          left: 10%;
          animation-delay: 0s;
        }

        .shape-2 {
          width: 60px;
          height: 60px;
          bottom: 30%;
          right: 15%;
          border-radius: 50%;
          animation-delay: -5s;
        }

        .shape-3 {
          width: 30px;
          height: 30px;
          top: 60%;
          left: 85%;
          animation-delay: -10s;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
          z-index: 2;
        }

        .hero-content {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
        }

        /* Badge */
        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(34, 211, 238, 0.1);
          padding: 10px 20px;
          border-radius: 50px;
          margin-bottom: 40px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .hero.visible .hero-badge {
          opacity: 1;
          transform: translateY(0);
        }

        .badge-dot {
          width: 8px;
          height: 8px;
          background: #22d3ee;
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        .badge-text {
          font-size: 0.9rem;
          font-weight: 500;
          color: #94a3b8;
        }

        /* Title */
        .title-wrapper {
          margin-bottom: 32px;
          overflow: hidden;
        }

        .hero-title {
          font-size: 4.2rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 0;
        }

        .title-line {
          display: block;
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .hero.visible .title-line-1 {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.2s;
        }

        .hero.visible .title-line-2 {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.4s;
        }

        .hero.visible .title-line-3 {
          opacity: 1;
          transform: translateY(0);
          transition-delay: 0.6s;
        }

        .highlight-text {
          color: #22d3ee;
          position: relative;
          display: inline-block;
        }

        .highlight-text::after {
          content: '';
          position: absolute;
          bottom: 5px;
          left: 0;
          width: 100%;
          height: 8px;
          background: linear-gradient(90deg, 
            rgba(34, 211, 238, 0.3), 
            rgba(59, 130, 246, 0.3));
          z-index: -1;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 1s ease 1s;
        }

        .hero.visible .highlight-text::after {
          transform: scaleX(1);
        }

        /* Tagline */
        .tagline {
          font-size: 1.5rem;
          line-height: 1.6;
          margin-bottom: 24px;
          color: #cbd5e1;
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease 0.8s, transform 0.8s ease 0.8s;
        }

        .hero.visible .tagline {
          opacity: 1;
          transform: translateY(0);
        }

        .company-name {
          color: #22d3ee;
          font-weight: 600;
        }

        /* Description */
        .description {
          font-size: 1.15rem;
          line-height: 1.8;
          margin-bottom: 48px;
          color: #94a3b8;
          max-width: 760px;
          margin-left: auto;
          margin-right: auto;
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease 1s, transform 0.8s ease 1s;
        }

        .hero.visible .description {
          opacity: 1;
          transform: translateY(0);
        }

        /* Stats */
        .hero-stats {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 32px;
          margin-bottom: 48px;
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease 1.2s, transform 0.8s ease 1.2s;
        }

        .hero.visible .hero-stats {
          opacity: 1;
          transform: translateY(0);
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .stat-divider {
          width: 1px;
          height: 40px;
          background: rgba(148, 163, 184, 0.2);
        }

        /* Buttons */
        .hero-actions {
          display: flex;
          gap: 20px;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 80px;
        }

        /* Primary Button */
        .btn-primary {
          padding: 18px 36px;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          color: #020617;
          border-radius: 12px;
          text-decoration: none;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          position: relative;
          overflow: hidden;
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.5s ease, transform 0.5s ease;
          border: none;
          cursor: pointer;
          box-shadow: 
            0 10px 30px rgba(34, 211, 238, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.1);
        }

        .btn-primary.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .btn-hover-effect {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(255, 255, 255, 0.2), 
            transparent);
          transition: left 0.6s ease;
        }

        .btn-primary:hover .btn-hover-effect {
          left: 100%;
        }

        .btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 
            0 15px 40px rgba(34, 211, 238, 0.4),
            0 0 0 1px rgba(255, 255, 255, 0.2);
        }

        .btn-icon {
          display: flex;
          align-items: center;
          transition: transform 0.3s ease;
        }

        .btn-primary:hover .btn-icon {
          transform: translateX(4px);
        }

        /* Secondary Button */
        .btn-secondary {
          padding: 18px 36px;
          background: transparent;
          color: #22d3ee;
          border: 2px solid rgba(34, 211, 238, 0.3);
          border-radius: 12px;
          text-decoration: none;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 12px;
          transition: all 0.3s ease;
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.5s ease 0.1s, transform 0.5s ease 0.1s, all 0.3s ease;
        }

        .btn-secondary.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .btn-secondary:hover {
          background: rgba(34, 211, 238, 0.1);
          border-color: #22d3ee;
          transform: translateY(-3px);
        }

        /* Video Button */
        .btn-video {
          padding: 18px 24px;
          background: transparent;
          color: #94a3b8;
          border: 2px solid rgba(148, 163, 184, 0.2);
          border-radius: 12px;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .btn-video:hover {
          color: #22d3ee;
          border-color: rgba(34, 211, 238, 0.3);
          transform: translateY(-3px);
        }

        .video-icon {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
        }

        /* Scroll Indicator */
        .scroll-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          opacity: 0;
          animation: fadeIn 0.8s ease 2s forwards;
        }

        .mouse {
          width: 34px;
          height: 56px;
          border: 2px solid rgba(148, 163, 184, 0.3);
          border-radius: 20px;
          position: relative;
        }

        .wheel {
          width: 4px;
          height: 8px;
          background: #22d3ee;
          border-radius: 2px;
          position: absolute;
          top: 10px;
          left: 50%;
          transform: translateX(-50%);
          animation: scroll 2s ease-in-out infinite;
        }

        .scroll-text {
          font-size: 0.9rem;
          color: #94a3b8;
          letter-spacing: 1px;
        }

        /* Animations */
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(3deg); }
          66% { transform: translateY(10px) rotate(-3deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }

        @keyframes scroll {
          0% { transform: translateX(-50%) translateY(0); opacity: 1; }
          100% { transform: translateX(-50%) translateY(20px); opacity: 0; }
        }

        @keyframes fadeIn {
          to { opacity: 1; }
        }

        /* Mobile Responsive */
        @media (max-width: 1024px) {
          .hero-title {
            font-size: 3.5rem;
          }

          .tagline {
            font-size: 1.3rem;
          }

          .hero-stats {
            gap: 24px;
          }

          .stat-number {
            font-size: 2rem;
          }
        }

        @media (max-width: 768px) {
          .hero {
            padding: 120px 0 60px;
          }

          .hero-title {
            font-size: 2.8rem;
          }

          .title-line {
            display: inline;
          }

          .tagline {
            font-size: 1.1rem;
          }

          .description {
            font-size: 1rem;
          }

          .hero-actions {
            flex-direction: column;
            gap: 16px;
          }

          .btn-primary,
          .btn-secondary,
          .btn-video {
            width: 100%;
            max-width: 300px;
            justify-content: center;
          }

          .hero-stats {
            flex-direction: column;
            gap: 20px;
          }

          .stat-divider {
            display: none;
          }

          .orb-1,
          .orb-2,
          .orb-3 {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 2.2rem;
          }

          .container {
            padding: 0 20px;
          }

          .hero-badge {
            padding: 8px 16px;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;